import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { SmartToy, DeleteOutline } from '@mui/icons-material';
import axios from 'axios';
import Chatbox from '../components/chat/Chatbox';

const Assistant = () => {

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [lastFailedMessage, setLastFailedMessage] = useState(null);

  // Clear chat history on page load (fresh session each visit)
  useEffect(() => {
    const clearAndStart = async () => {
      try {
        await axios.delete('/chat');
      } catch (err) {
        // Ignore — may not have a session yet
      }
      setMessages([]);
    };
    clearAndStart();
  }, []);

  // Clear conversation manually
  const handleClearChat = async () => {
    try {
      await axios.delete('/chat');
    } catch (err) {
      // Ignore
    }
    setMessages([]);
    setError(null);
    setRateLimitInfo(null);
    setLastFailedMessage(null);
  };

  // Send message to the chat API
  const handleSendMessage = async (messageText) => {
    if (loading) return;

    setRateLimitInfo(null);
    setError(null);
    setLastFailedMessage(null);

    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);
    setLoading(true);

    try {
      const response = await axios.post('/chat', { message: messageText });

      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.id !== tempUserMessage.id);
        return [
          ...withoutTemp,
          {
            id: response.data.userMessageId || tempUserMessage.id,
            role: 'user',
            content: messageText,
            createdAt: response.data.userCreatedAt || tempUserMessage.createdAt,
          },
          {
            id: response.data.id,
            role: 'assistant',
            content: response.data.content,
            createdAt: response.data.createdAt,
          },
        ];
      });
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) return;

      if (err.response?.status === 429) {
        const retryMinutes = err.response.data?.retryAfterMinutes || 1;
        setRateLimitInfo({ minutes: retryMinutes });
        setError(`Rate limit reached. Please wait ${retryMinutes} minute${retryMinutes > 1 ? 's' : ''} before sending another message.`);
      } else {
        setError(err.response?.data?.message || 'Failed to get a response. Please try again.');
      }

      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      setLastFailedMessage(messageText);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (lastFailedMessage) {
      handleSendMessage(lastFailedMessage);
    }
  };

  // Rate limit countdown
  useEffect(() => {
    if (!rateLimitInfo) return;

    const interval = setInterval(() => {
      setRateLimitInfo((prev) => {
        if (!prev) return null;
        const newMinutes = prev.minutes - (1 / 60);
        if (newMinutes <= 0) {
          setError(null);
          return null;
        }
        return { minutes: newMinutes };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [rateLimitInfo]);

  const formatCountdown = (minutes) => {
    if (!minutes || minutes <= 0) return '';
    const totalSeconds = Math.ceil(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* Page Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <SmartToy color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            AI Trading Assistant
          </Typography>
        </Box>
        {messages.length > 0 && (
          <Tooltip title="Clear conversation">
            <IconButton onClick={handleClearChat} color="default">
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Rate limit countdown alert */}
      {rateLimitInfo && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rate limit reached. You can send another message in {formatCountdown(rateLimitInfo.minutes)}.
        </Alert>
      )}

      {/* Chatbox */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Chatbox
          messages={messages}
          onSendMessage={handleSendMessage}
          loading={loading}
          error={!rateLimitInfo ? error : null}
          onRetry={handleRetry}
          disabled={false}
        />
      </Box>
    </Container>
  );
};

export default Assistant;
